import axios from "axios";
import toast from "react-hot-toast";
import { APPLICATION_KEY } from "./Configurations/Urls";
import {
  forceLogoutOnInactiveAccess,
  isInactiveOrgAccessMessage,
  shouldForceLogoutFromAccessError,
} from "./setupAxiosAuthInterceptor";

function mergeAuthHeaders(initHeaders) {
  const headers = new Headers();

  if (initHeaders) {
    if (initHeaders instanceof Headers) {
      initHeaders.forEach((value, key) => headers.set(key, value));
    } else if (Array.isArray(initHeaders)) {
      initHeaders.forEach(([key, value]) => headers.set(key, String(value)));
    } else {
      Object.entries(initHeaders).forEach(([key, value]) => {
        if (value != null) headers.set(key, String(value));
      });
    }
  }

  const token = localStorage.getItem("token");
  if (token && !headers.has("Authorization") && !headers.has("authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  if (APPLICATION_KEY && !headers.has("X-Application-Key")) {
    headers.set("X-Application-Key", APPLICATION_KEY);
  }

  return headers;
}

/**
 * Attaches JWT and product key to every axios / fetch request.
 * Also logs out when fetch gets an inactive-org 403 (axios handled separately).
 */
export function setupAxiosApplicationKey() {
  axios.interceptors.request.use((config) => {
    config.headers = config.headers || {};

    const token = localStorage.getItem("token");
    if (token && !config.headers.Authorization && !config.headers.authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (APPLICATION_KEY && !config.headers["X-Application-Key"]) {
      config.headers["X-Application-Key"] = APPLICATION_KEY;
    }

    return config;
  });

  if (typeof window !== "undefined" && !window.__hrFetchPatched) {
    const originalFetch = window.fetch.bind(window);
    window.fetch = async (input, init = {}) => {
      const headers = mergeAuthHeaders(init.headers);
      const response = await originalFetch(input, { ...init, headers });

      const url = typeof input === "string" ? input : input?.url || "";
      const isLoginUrl =
        String(url).includes("/api/auth/login") ||
        String(url).includes("/auth/login") ||
        String(url).includes("/api/auth/token-login");

      if (!isLoginUrl) {
        if (response.status === 403) {
          try {
            const clone = response.clone();
            const data = await clone.json();
            const msg = data?.message;
            const fakeError = { response: { status: 403, data } };
            if (shouldForceLogoutFromAccessError(fakeError)) {
              forceLogoutOnInactiveAccess(msg);
            }
          } catch {
            // ignore non-JSON 403 bodies
          }
        } else if (response.status === 401) {
          const token = localStorage.getItem("token");
          if (token) {
            // Delayed to allow the current response to be consumed first
            setTimeout(() => {
              toast.error("Session expired. Please log in again.");
              forceLogoutOnInactiveAccess(null);
            }, 0);
          }
        }
      }

      return response;
    };
    window.__hrFetchPatched = true;
  }
}
